import ENDPOINTS from 'api/endpoints';
import { doGet, doPatch, doPut } from 'api/request';
import { LOGGER_TYPE } from 'api/types';
import { generateNextPath } from 'lib/routeHelper';
import type { BargeIn } from 'utils/validators/settings';
import { DEFAULT_CONTENT_FILTER } from './constants';
import type {
  AsrOptionsResponse,
  AssistantConfig,
  ContentFilter,
  ModelOptionsResponse,
  VoiceOptionsResponse,
} from './types';

export const getVoiceOptions = (accountId: string, projectId: string) => {
  return doGet<VoiceOptionsResponse>('voices', LOGGER_TYPE.JUPITER, {
    params: { account_id: accountId, project_id: projectId },
  });
};

export const updateAssistantVoice = ({
  accountId,
  projectId,
  voiceId,
}: {
  accountId: string;
  projectId: string;
  voiceId: string;
}) => {
  return doPut<AssistantConfig>(
    generateNextPath(ENDPOINTS.VOICE, { accountId, projectId }),
    LOGGER_TYPE.JUPITER,
    {
      voice_id: voiceId,
    },
    { version: 'v2' },
  );
};

export const getModelOptions = (accountId: string) => {
  return doGet<ModelOptionsResponse>('models', LOGGER_TYPE.JUPITER, {
    params: { account_id: accountId },
  });
};

export const getAsrOptions = () => {
  // No account_id param for getting global ASR options
  return doGet<AsrOptionsResponse>('asr', LOGGER_TYPE.JUPITER, {
    version: 'v2',
  });
};

export const updateAssistantModel = ({
  accountId,
  projectId,
  modelId,
}: {
  accountId: string;
  projectId: string;
  modelId: string;
}) => {
  return doPut<AssistantConfig>(
    generateNextPath(ENDPOINTS.MODEL, { accountId, projectId }),
    LOGGER_TYPE.JUPITER,
    {
      model_id: modelId,
    },
    { version: 'v2' },
  );
};

export const getAssistantConfig = (accountId: string, projectId: string) => {
  return doGet<AssistantConfig>(
    generateNextPath(ENDPOINTS.CONFIG, { accountId, projectId }),
    LOGGER_TYPE.JUPITER,
    { version: 'v2' },
  ).then(config => {
    if (config.content_filter === null) {
      config.content_filter = DEFAULT_CONTENT_FILTER;
    }
    return config;
  });
};

export const updateAssistantConfig = ({
  accountId,
  projectId,
  config,
}: {
  accountId: string;
  projectId: string;
  config: Partial<AssistantConfig>;
}) => {
  return doPatch<AssistantConfig>(
    generateNextPath(ENDPOINTS.CONFIG, { accountId, projectId }),
    LOGGER_TYPE.JUPITER,
    config,
    { version: 'v2' },
  );
};

export const updateAssistantContentFilter = ({
  accountId,
  projectId,
  contentFilter,
}: {
  accountId: string;
  projectId: string;
  contentFilter: ContentFilter;
}) => {
  return doPatch<AssistantConfig>(
    generateNextPath(ENDPOINTS.CONFIG, { accountId, projectId }),
    LOGGER_TYPE.JUPITER,
    {
      content_filter: {
        config: contentFilter,
        type: 'AZURE',
      },
    },
    { version: 'v2' },
  );
};

export const updateAssistantBargeIn = ({
  accountId,
  projectId,
  bargeInEnabled,
}: {
  accountId: string;
  projectId: string;
} & BargeIn) => {
  return doPatch<AssistantConfig>(
    generateNextPath(ENDPOINTS.CONFIG, { accountId, projectId }),
    LOGGER_TYPE.JUPITER,
    { barge_in_enabled: bargeInEnabled },
    { version: 'v2' },
  );
};
